/**
 * Alat saturasi air — metodenya, dan konstanta yang dibaca metode itu.
 *
 * Sebangun dengan ketiga alat sebelumnya, dan dipakai oleh dua layar yang sama:
 * rel parameter di ruang kerja dan tahap keempat di rel alur deterministik.
 *
 * Yang khas pada tahap ini adalah bahwa metodenya sudah bisa dipilih sejak lama
 * — rel ruang kerja punya menu "Model saturasi" — tetapi tiga dari tujuh angka
 * yang dibaca keempat metodenya tidak bisa dicapai dari mana pun: Rsh, Qv dan
 * suhunya ada di mesin, ada di API, dan tidak punya satu kotak pun. Menu yang
 * menawarkan Waxman–Smits tanpa menawarkan Qv adalah menu yang menawarkan satu
 * angka bawaan dengan nama sebuah metode.
 */
import { useState } from "react";

import type { ParameterValues } from "../types";
import {
  CurveSourceFields,
  channelsRead,
  sameParam,
  type AvailableCurve,
} from "./CurveSource";
import { Button, Dropdown, Modal, NumberField, ParamCaption } from "./ui";

/**
 * Cara saturasi air dihitung.
 *
 * Urutannya urutan pilihan, bukan abjad: Poupon–Leveaux lebih dulu sebab ia
 * bawaan aplikasi ini dan alasannya ada di mesin — ia dibangun untuk pasir
 * serpihan Tersier berair tawar, yang persis penampang Kutai yang dibawa
 * aplikasi ini. Petunjuknya menyebut apa yang membedakan pilihan itu dari yang
 * di atasnya, bukan kurva yang dibacanya: keempatnya membaca RT dan PHIE yang
 * sama, dan yang berbeda hanyalah suku hantaran mana yang diakui selain air.
 */
export const SW_METHODS = [
  { value: "indonesia", label: "Indonesia (Poupon–Leveaux)", hint: "shale term" },
  { value: "archie", label: "Archie", hint: "clean sand" },
  { value: "simandoux", label: "Simandoux", hint: "shale term" },
  { value: "waxman_smits", label: "Waxman–Smits", hint: "clay cations" },
];

/** Rumus tiap pilihan, ditulis sebagaimana ia dihitung di mesin. */
export const SW_FORMULAS: Record<string, string> = {
  archie: "Sw = (a · Rw / (PHIE^m · Rt))^(1/n)",
  indonesia: "1/√Rt = (Vsh^(1−Vsh/2)/√Rsh + √(PHIE^m/(a·Rw))) · Sw^(n/2)",
  simandoux: "1/Rt = PHIE^m·Sw^n/(a·Rw·(1−Vsh)) + Vsh·Sw^(n/2)/Rsh",
  waxman_smits: "1/Rt = PHIE^m·Sw^n/a · (1/Rw + B·Qv/Sw)",
};

/**
 * Apa yang diakui tiap metode selain air, dan apa akibatnya bagi pemakainya.
 *
 * Bukan sejarah persamaannya: yang perlu dibaca sebelum memilih adalah di rock
 * mana pilihan itu masih berlaku. Ketiganya sepakat di pasir bersih dan
 * berselisih justru di tempat pilihan itu menentukan sesuatu.
 */
export const SW_NOTES: Record<string, string> = {
  archie:
    "Reads no Vsh at all: every path of conduction is taken to be formation " +
    "water. In shaly sand it reads too wet, because the clay that conducts " +
    "alongside is counted as water.",
  indonesia:
    "Poupon–Leveaux, built for fresh-water Tertiary shaly sand — exactly the " +
    "section this application carries. Its shale term stays under control up " +
    "to high Vsh, so it is the only one here that still reads over an interval " +
    "shalier than it is sandy.",
  simandoux:
    "A sand equation, not a shale equation. Its sand term is divided by " +
    "(1 − Vsh), so it slides towards Sw zero as Vsh approaches one and does " +
    "not evaluate at all in pure shale. Above Vsh 0.8 it reads far below " +
    "Poupon–Leveaux on the same sample; use it in the sand alone.",
  waxman_smits:
    "Accounts for clay-cation conduction through Qv rather than through Vsh, " +
    "so it demands Qv from core or from a CEC correlation. Sw appears on both " +
    "sides of its equation, so the engine iterates it from the Archie answer " +
    "until it settles.",
};

/** Rsh dibaca dua metode, Qv dan suhunya dibaca satu, Archie tidak satu pun. */
const READS_SHALE = new Set(["indonesia", "simandoux"]);

/**
 * Metode beserta angka yang dibacanya, tanpa menyimpan apa pun sendiri.
 *
 * a, m, n dan Rw selalu muncul sebab keempat metodenya memang membaca
 * keempatnya. Yang muncul dan menghilang hanyalah suku selain air — dan itulah
 * satu-satunya yang membedakan pilihan-pilihan ini.
 */
export function SwFields({
  params,
  available,
  onChange,
  disabled = false,
}: {
  params: ParameterValues;
  /** Mnemonik yang ada di set kurva aktif sumur ini; tanpa itu tidak ada
   *  pemilih sumber sama sekali — rel ruang kerja memanggil `SwFields` di
   *  tempat yang belum memuat daftar kurvanya. */
  available?: AvailableCurve[];
  onChange: (
    key: keyof ParameterValues,
    value: number | string | boolean | Record<string, string>,
  ) => void;
  disabled?: boolean;
}) {
  const method = String(params.sw_method ?? "indonesia");
  return (
    <>
      <ParamCaption>Method</ParamCaption>
      <Dropdown
        value={method}
        options={SW_METHODS}
        onChange={(value) => onChange("sw_method", value)}
        width="100%"
        disabled={disabled}
      />
      <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-4)" }}>
        {SW_FORMULAS[method] ?? ""}
      </span>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <NumberField
          label="a (tortuosity)"
          value={Number(params.a)}
          step={0.01}
          onChange={(v) => onChange("a", v)}
          disabled={disabled}
        />
        <NumberField
          label="m (cementation)"
          value={Number(params.m)}
          step={0.01}
          onChange={(v) => onChange("m", v)}
          disabled={disabled}
        />
        <NumberField
          label="n (saturation)"
          value={Number(params.n)}
          step={0.01}
          onChange={(v) => onChange("n", v)}
          disabled={disabled}
        />
        <NumberField
          label="Rw"
          unit="ohm·m"
          value={Number(params.rw)}
          step={0.001}
          onChange={(v) => onChange("rw", v)}
          disabled={disabled}
        />
      </div>

      {READS_SHALE.has(method) && (
        <NumberField
          label="Rsh"
          unit="ohm·m"
          value={Number(params.r_shale)}
          step={0.1}
          min={0}
          onChange={(v) => onChange("r_shale", v)}
          disabled={disabled}
        />
      )}

      {method === "waxman_smits" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <NumberField
            label="Qv"
            unit="meq/ml"
            value={Number(params.qv)}
            step={0.01}
            min={0}
            onChange={(v) => onChange("qv", v)}
            disabled={disabled}
          />
          {/* Suhunya hanya membengkokkan B, hantaran ekivalen kationnya. Ia
              tidak mengoreksi Rw — Rw yang dimasukkan di atas adalah Rw pada
              suhu formasi, sebagaimana ketiga metode lain membacanya. */}
          <NumberField
            label="Formation T"
            unit="°C"
            value={Number(params.rw_temperature)}
            step={1}
            onChange={(v) => onChange("rw_temperature", v)}
            disabled={disabled}
          />
        </div>
      )}

      {available && (
        <CurveSourceFields
          params={params}
          available={available}
          channels={channelsRead(4, params)}
          onChange={(sources) => onChange("curve_sources", sources)}
          disabled={disabled}
        />
      )}

      <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
        {SW_NOTES[method] ?? ""}
      </span>
    </>
  );
}

/**
 * Kunci yang boleh berangkat dari jendela ini; sisanya bukan urusan Sw.
 *
 * PHIE tidak ada di sini meskipun saturasi membacanya, dengan alasan yang sama
 * seperti lengkung sonik pada jendela sebelumnya: porositas efektif dipilih di
 * tahap tiga, tempat ia pertama kali menentukan sesuatu. Dua jendela yang
 * sama-sama boleh mengubah PHIE adalah dua tahap yang bisa berselisih tentang
 * kurva yang sama.
 */
const SW_KEYS: (keyof ParameterValues)[] = [
  "sw_method", "a", "m", "n", "rw", "r_shale", "qv", "rw_temperature",
  // Kurva yang memerankan RT. Ia satu-satunya kunci di daftar ini yang bukan
  // milik tahap empat sendirian — pemetaannya satu untuk seluruh alur — tetapi
  // yang **disunting** di jendela ini hanyalah salurannya sendiri, dan
  // `withSource` menyalin sisanya apa adanya.
  "curve_sources",
];

/**
 * Tahap saturasi air sebagai jendelanya sendiri.
 *
 * Aturannya sama dengan ketiga jendela sebelumnya: yang dikirim pulang hanyalah
 * yang benar-benar diubah, sebab tiap run dengan override menerbitkan versi
 * parameter set baru.
 */
export function SwDialog({
  params,
  available,
  busy = false,
  disabled = false,
  onClose,
  onRun,
}: {
  params: ParameterValues;
  available?: AvailableCurve[];
  busy?: boolean;
  disabled?: boolean;
  onClose: () => void;
  onRun: (overrides: Partial<ParameterValues>) => void;
}) {
  const [draft, setDraft] = useState<Partial<ParameterValues>>({});
  const merged = { ...params, ...draft } as ParameterValues;
  const changed = Object.fromEntries(
    SW_KEYS.filter((key) => key in draft && !sameParam(draft[key], params[key])).map(
      (key) => [key, draft[key]],
    ),
  ) as Partial<ParameterValues>;

  return (
    <Modal
      title="4 · Water saturation"
      width={440}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={disabled}
            onClick={() => onRun(changed)}
          >
            Compute water saturation
          </Button>
        </>
      }
    >
      <SwFields
        params={merged}
        available={available}
        onChange={(key, value) => setDraft((current) => ({ ...current, [key]: value }))}
        disabled={disabled || busy}
      />
      <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
        {"The whole deterministic workflow is run again. Permeability is read " +
          "from this saturation, the SW cutoff decides net pay on the same " +
          "curve, and the HCPV of each zone is computed from its mean."}
      </span>
    </Modal>
  );
}
